import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Search,
  CheckCircle,
  AlertCircle,
  Calendar
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = [
  { value: 1, label: 'Janvier' },
  { value: 2, label: 'Février' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' },
  { value: 8, label: 'Août' },
  { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Décembre' }
];

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }
  return years;
};

const isUpToDate = (month, year) => {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
  const prevYear = currentMonth === 1 ? currentYear - 1 : currentYear;
  
  const statusDate = new Date(year, month - 1, 1);
  const thresholdDate = new Date(prevYear, prevMonth - 1, 1);
  
  return statusDate >= thresholdDate;
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const StatWidget = ({ icon: Icon, label, value, color }) => (
  <div className="widget-card">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-2xl font-bold tabular-nums">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </div>
  </div>
);

const MiseAJour = () => {
  const [clients, setClients] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [stats, setStats] = useState({ up_to_date: 0, late: 0, total: 0, percentage: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('Tous');
  const [editingClient, setEditingClient] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, statusesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/clients`),
        axios.get(`${API_URL}/client-status`),
        axios.get(`${API_URL}/client-status-stats`)
      ]);
      
      setClients(clientsRes.data);
      
      // Convert statuses array to map
      const statusMap = {};
      statusesRes.data.forEach(s => {
        statusMap[s.client_id] = s;
      });
      setStatuses(statusMap);
      
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (clientId, month, year) => {
    try {
      await axios.put(`${API_URL}/client-status/${clientId}`, {
        up_to_date_month: month,
        up_to_date_year: year
      });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleNotesUpdate = async (clientId, notes) => {
    try {
      await axios.put(`${API_URL}/client-status/${clientId}`, { notes });
      // Update local state without full refresh
      setStatuses(prev => ({
        ...prev,
        [clientId]: { ...prev[clientId], notes }
      }));
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        if (!client.company_name.toLowerCase().includes(searchLower)) {
          return false;
        }
      }
      
      // Status filter
      if (statusFilter !== 'Tous') {
        const status = statuses[client.id];
        const upToDate = status ? isUpToDate(status.up_to_date_month, status.up_to_date_year) : false;
        if (statusFilter === 'À jour' && !upToDate) return false;
        if (statusFilter === 'En retard' && upToDate) return false;
      }
      
      return true;
    });
  }, [clients, statuses, searchQuery, statusFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mise-a-jour-page">
      <Toaster position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Mise à jour</h1>
      </div>

      {/* Stats widgets */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatWidget 
          icon={CheckCircle}
          label="Clients à jour"
          value={stats.up_to_date}
          color="bg-emerald-50 text-emerald-600"
        />
        <StatWidget 
          icon={AlertCircle}
          label="Clients en retard"
          value={stats.late}
          color="bg-red-50 text-red-600"
        />
        <div className="widget-card">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
              <Calendar size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold tabular-nums">{stats.percentage}%</p>
              <p className="text-sm text-slate-500">Taux de mise à jour</p>
            </div>
          </div>
          <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${stats.percentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            type="text"
            placeholder="Rechercher un client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-11 rounded-lg"
            data-testid="search-client-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="filter-status">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Tous">Tous</SelectItem>
            <SelectItem value="À jour">À jour</SelectItem>
            <SelectItem value="En retard">En retard</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {filteredClients.length === 0 ? (
          <div className="empty-state">
            <Calendar size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">Aucun client trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="client-status-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Comptabilité à jour jusqu'à</th>
                  <th>Statut</th>
                  <th>Dernière modification</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => {
                  const status = statuses[client.id];
                  const month = status?.up_to_date_month || 1;
                  const year = status?.up_to_date_year || new Date().getFullYear();
                  const upToDate = isUpToDate(month, year);
                  
                  return (
                    <tr key={client.id} data-testid={`client-status-row-${client.id}`}>
                      <td className="font-medium">{client.company_name}</td>
                      <td>
                        <div className="flex gap-2 items-center">
                          <Select 
                            value={month.toString()} 
                            onValueChange={(v) => handleStatusUpdate(client.id, parseInt(v), year)}
                          >
                            <SelectTrigger className="w-32 h-9" data-testid={`month-select-${client.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {MONTHS.map(m => (
                                <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select 
                            value={year.toString()} 
                            onValueChange={(v) => handleStatusUpdate(client.id, month, parseInt(v))}
                          >
                            <SelectTrigger className="w-24 h-9" data-testid={`year-select-${client.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getYearOptions().map(y => (
                                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </td>
                      <td>
                        <Badge className={upToDate ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                          {upToDate ? 'À jour' : 'En retard'}
                        </Badge>
                      </td>
                      <td className="text-sm text-slate-500">
                        {status?.last_modified_at ? (
                          <>
                            {formatDateTime(status.last_modified_at)}
                            <br />
                            <span className="text-xs">{status.last_modified_by_name}</span>
                          </>
                        ) : '-'}
                      </td>
                      <td>
                        <Input
                          type="text"
                          value={status?.notes || ''}
                          onChange={(e) => {
                            // Update local state immediately
                            setStatuses(prev => ({
                              ...prev,
                              [client.id]: { ...prev[client.id], notes: e.target.value }
                            }));
                          }}
                          onBlur={(e) => handleNotesUpdate(client.id, e.target.value)}
                          placeholder="Ajouter une note..."
                          className="h-9 text-sm"
                          data-testid={`notes-input-${client.id}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiseAJour;
